import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { scannerRequestStateAtom } from "../../../components/Atoms/atoms";
import { addLeadingZerosQuestions } from "../utils/common";
import { Divider } from "@mui/material";
import CustomTooltip from "../components/CustomTooltip";

export default function MRScor(props) {
	const [state, setState] = useAtom(scannerRequestStateAtom);

	const [errorState, setErrorState] = useState({
		lqNum: false,
		hqNum: false,
		qLen: false,
		wholeExamMR: false,
		multipleResponseWeighted: false,
		weightingMultiple: false,
		schema: false,
		notes: false,
		classNum: false,
		examTotalMarks: false,
	});

	useEffect(() => {
		//calculate NOP values as 1/3 of total marks
		if (
			state.hqNum &&
			state.lqNum &&
			state.lqNum !== "" &&
			state.hqNum !== ""
		) {
			let NOP = Math.floor((Number(state.hqNum) - Number(state.lqNum) + 1) / 3);

			let formattedNOP = addLeadingZerosQuestions(NOP.toString());
			setState({
				...state,
				n: formattedNOP.split("")[0],
				o: formattedNOP.split("")[1],
				p: formattedNOP.split("")[2],
			});
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.hqNum, state.lqNum]);

	useEffect(() => {
		//set state.numMultipleResponse and state.numSingleResponse based on selection
		if (state.wholeExamMR === "No" && state.multipleResponseQuestions) {
			setState((prev) => ({
				...prev,
				numMultipleResponse: state.multipleResponseQuestions.length,
				numSingleResponse:
					Number(state.hqNum) -
					Number(state.lqNum) +
					1 -
					state.multipleResponseQuestions.length,
			}));
		} else if (state.wholeExamMR === "Yes") {
			setState((prev) => ({
				...prev,
				numMultipleResponse: Number(state.hqNum) - Number(state.lqNum) + 1,
				numSingleResponse: 0,
			}));
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		state.wholeExamMR,
		state.multipleResponseQuestions,
		state.lqNum,
		state.hqNum,
	]);

	useEffect(() => {
		// Only update total marks if all required values exist.
		if (state.lqNum && state.hqNum && state.qLen && state.wholeExamMR) {
			const lq = Number(state.lqNum);
			const hq = Number(state.hqNum);
			const totalQuestions = hq - lq + 1;
			let totalMarks = 0;

			if (state.wholeExamMR === "Yes") {
				// Entire exam is multiple response: use the single-response weighting for all questions.
				if (state.weightingMultiple) {
					totalMarks = totalQuestions * Number(state.weightingMultiple);
				}
			} else if (state.wholeExamMR === "No") {
				// Mixed exam: use weighting for multiple response and single response.
				// Ensure we have numeric values for numMultipleResponse and numSingleResponse.
				const numMR = state.numMultipleResponse
					? Number(state.numMultipleResponse)
					: 0;
				// If numSingleResponse isn’t set, assume the rest are single response.
				const numSingle = state.numSingleResponse
					? Number(state.numSingleResponse)
					: totalQuestions - numMR;
				if (state.weightingMultiple && state.multipleResponseWeighted) {
					totalMarks =
						numMR * Number(state.weightingMultiple) +
						numSingle * Number(state.multipleResponseWeighted);
				}
			}

			setState((prev) => ({ ...prev, examTotalMarks: totalMarks }));
		}
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		state.lqNum,
		state.hqNum,
		state.qLen,
		state.multipleResponseWeighted,
		state.weightingMultiple,
		state.wholeExamMR,
		state.numMultipleResponse,
		state.numSingleResponse,
		state.multipleResponseQuestions,
	]);

	// -------------------------------
	// HANDLERS FOR INPUT CHANGES & SUBMISSION
	// -------------------------------
	const handleMrScorChange = (event) => {
		const { name, value } = event.target;
		setState((prev) => ({ ...prev, [name]: value }));

		if (name === "wholeExamMR") {
			setErrorState((prev) => ({ ...prev, wholeExamMR: false }));
		}
		if (name === "multipleResponseWeighted") {
			setErrorState((prev) => ({ ...prev, multipleResponseWeighted: false }));
		}
		if (name === "weightingMultiple") {
			setErrorState((prev) => ({ ...prev, weightingMultiple: false }));
		}
		if (name === "schema") {
			setErrorState((prev) => ({ ...prev, schema: false }));
		}

		if (name === "lqNum") {
			setErrorState((prev) => ({ ...prev, lqNum: false }));
		}

		if (name === "hqNum") {
			setErrorState((prev) => ({ ...prev, hqNum: false }));
		}
	};

	const gradingSchemeOptions = ["Rights Minus Wrongs", "All or None", "Other"];

	const schemaTooltipObjects = {
		"Rights Minus Wrongs": {
			title: "Rights Minus Wrongs",
			description:
				"The most common marking scheme for Multiple Response exams. With this scheme, the student's grade for each question is calculated as the number of correct answers they selected minus the number of incorrect answers they selected.",
			example: `Question 5 on your exam has 3 correct answers and 2 incorrect answers. A student selects 2 correct answers and 1 incorrect answer. Their grade for this question would be 2 (correct) - 1 (incorrect) = 1/3 (mark received/total correct for this question).`,
		},
		"All or None": {
			title: "All or None",
			description:
				"With this scheme, marks are awarded to the student only if they select all the correct answers and none of the incorrect answers.",
			example: `Question 5 on your exam has three correct answers and two incorrect answers. A student selects all three correct answers and one of the incorrect answers. Their grade for this question would be 0.`,
		},
		Other: {
			title: "Other",
			description: "Please specify your grading scheme text box below.",
		},
	};

	return (
		<div className="grid grid-cols-2 gap-4 w-full">
			{((state.wholeExamMR === "Yes" && state.weightingMultiple) ||
				(state.wholeExamMR === "No" &&
					state.multipleResponseWeighted &&
					state.weightingMultiple)) && (
				<div className="flex flex-col col-span-2">
					<label className="font-medium text-primary">
						Current Configuration:
					</label>
					{/* <div className="flex gap-2">Number of Questions: {numQuestions || "N/A"}</div>
					<div className="flex gap-2">Number of Answer Options: {state.qLen}</div> */}
					<div className="flex gap-2">
						Number of Multiple Response Questions: {state.numMultipleResponse}
					</div>
					{state.wholeExamMR === "No" && (
						<div className="flex gap-2">
							Number of Single Response Questions: {state.numSingleResponse}
						</div>
					)}
					<div className="flex gap-2">
						Exam is out of {state.examTotalMarks || "N/A"} total marks.
					</div>
				</div>
			)}
			<Divider />
			<div className="flex flex-col col-span-2 pb-4">
				<label
					htmlFor="schema"
					className="font-medium text-primary flex items-center gap-2 pb-2"
				>
					Which grading scheme would you like to use?*
					<span className="text-xs text-gray-500">
						(Hover over each option for an explanation)
					</span>
				</label>
				<div className="flex flex-col gap-2">
					{gradingSchemeOptions.map((option) => (
						<CustomTooltip
							key={option}
							title={schemaTooltipObjects[option].title}
							description={schemaTooltipObjects[option].description}
							example={schemaTooltipObjects[option].example}
						>
							<button
								key={option}
								className={`flex items-center gap-2 p-2 border rounded mb-1 disabled:bg-gray-300 disabled:cursor-not-allowed ${
									state.schema === option
										? "bg-primary text-white"
										: "bg-white border-gray-300 hover:bg-gray-50 text-gray-500"
								}`}
								onClick={() => setState({ ...state, schema: option })}
							>
								{option}
							</button>
						</CustomTooltip>
					))}
				</div>

				{state.schema === "Other" && (
					<textarea
						id="schemaNotes"
						name="schemaNotes"
						placeholder="Please specify your alternative grading scheme here (REQUIRED)"
						value={state.schemaNotes || ""}
						onChange={handleMrScorChange}
						className={`mt-1 p-2  border rounded ${
							errorState.schemaNotes ? "border-red-500" : "border-gray-300"
						}`}
					/>
				)}
			</div>
		</div>
	);
}
